/**
 * Tree-projection helpers for the scope panel.
 *
 * The server returns a flat list of v2-enabled instances (each with
 * its tagged scenarios) plus the user's current scope envelope.
 * The panel projects these into two trees: In Scope (the active
 * subset) and Available (everything else).
 */
import { ScopeEnvelope, ScopeOptionsInstance } from '../api';

import {
  ScenarioKey,
  ScopeTreeInstance,
  ScopeTreeModel,
  ScopeTreeScenario,
  scenarioKey,
} from './types';

/**
 * Set of "this scenario is in scope" keys for fast membership checks.
 */
export function buildInScopeKeySet(scope: ScopeEnvelope): Set<ScenarioKey> {
  const set = new Set<ScenarioKey>();
  for (const entry of scope.entries) {
    set.add(scenarioKey(entry.model_instance_id, entry.scenario_id));
  }
  return set;
}

/**
 * Project (options, scope) into the In Scope tree.
 * Empty model and instance branches are pruned.
 */
export function projectInScope(
  options: ScopeOptionsInstance[],
  inScopeKeys: Set<ScenarioKey>
): ScopeTreeModel[] {
  return projectTree(options, (sc) =>
    inScopeKeys.has(scenarioKey(sc.parentInstanceId, sc.scenario_id))
  );
}

/**
 * Project (options, scope) into the Available tree (complement of In Scope).
 * Empty model and instance branches are pruned. Optional filter for
 * the §5 search input.
 */
export function projectAvailable(
  options: ScopeOptionsInstance[],
  inScopeKeys: Set<ScenarioKey>,
  filterText: string
): ScopeTreeModel[] {
  const f = filterText.trim().toLowerCase();
  const terms = f.length ? f.split(/\s+/) : [];
  return projectTree(options, (sc) => {
    if (inScopeKeys.has(scenarioKey(sc.parentInstanceId, sc.scenario_id))) {
      return false;
    }
    if (terms.length === 0) return true;
    const haystack = [
      sc.forecast_model_name,
      sc.model_instance_name,
      sc.scenario_name,
      sc.tag ?? '',
    ]
      .join(' ')
      .toLowerCase();
    return terms.every((t) => haystack.includes(t));
  });
}

interface ScenarioRowInContext {
  parentInstanceId: number;
  scenario_id: number;
  scenario_name: string;
  tag: string | null;
  forecast_model_name: string;
  model_instance_name: string;
}

function projectTree(
  options: ScopeOptionsInstance[],
  predicate: (sc: ScenarioRowInContext) => boolean
): ScopeTreeModel[] {
  // Group by forecast_model_id, preserving server sort.
  const modelOrder: number[] = [];
  const modelById: Record<number, ScopeTreeModel> = {};

  for (const instance of options) {
    const matchedScenarios: ScopeTreeScenario[] = instance.scenarios
      .filter((sc) =>
        predicate({
          parentInstanceId: instance.model_instance_id,
          scenario_id: sc.scenario_id,
          scenario_name: sc.scenario_name,
          tag: sc.tag,
          forecast_model_name: instance.forecast_model_name,
          model_instance_name: instance.model_instance_name,
        })
      )
      .map((sc) => ({
        scenario_id: sc.scenario_id,
        scenario_name: sc.scenario_name,
        tag: sc.tag,
        forecast_model_id: instance.forecast_model_id,
        forecast_model_name: instance.forecast_model_name,
        model_instance_id: instance.model_instance_id,
        model_instance_name: instance.model_instance_name,
      }));

    if (matchedScenarios.length === 0) continue;

    // Sort scenarios by name (case-insensitive locale) regardless of
    // server order, so In Scope and Available trees are stable across
    // toggle order and re-sorts on every set change.
    matchedScenarios.sort((a, b) =>
      a.scenario_name.localeCompare(b.scenario_name, undefined, {
        sensitivity: 'base',
      })
    );

    const instanceNode: ScopeTreeInstance = {
      forecast_model_id: instance.forecast_model_id,
      forecast_model_name: instance.forecast_model_name,
      model_instance_id: instance.model_instance_id,
      model_instance_name: instance.model_instance_name,
      scenarios: matchedScenarios,
    };

    if (!(instance.forecast_model_id in modelById)) {
      modelById[instance.forecast_model_id] = {
        forecast_model_id: instance.forecast_model_id,
        forecast_model_name: instance.forecast_model_name,
        instances: [],
      };
      modelOrder.push(instance.forecast_model_id);
    }
    modelById[instance.forecast_model_id].instances.push(instanceNode);
  }

  // Sort by name at each level so the tree is stable across server
  // sort changes and across toggle-induced set changes.
  const collator = (a: string, b: string) =>
    a.localeCompare(b, undefined, { sensitivity: 'base' });
  const models = modelOrder
    .map((id) => modelById[id])
    .sort((a, b) => collator(a.forecast_model_name, b.forecast_model_name));
  for (const model of models) {
    model.instances.sort((a, b) =>
      collator(a.model_instance_name, b.model_instance_name)
    );
  }
  return models;
}

/**
 * Count scenarios across a model tree (for section headers).
 */
export function countScenarios(tree: ScopeTreeModel[]): number {
  let n = 0;
  for (const model of tree) {
    for (const instance of model.instances) {
      n += instance.scenarios.length;
    }
  }
  return n;
}
